import type {Location, NavigateOptions} from "react-router";
import {SESSION_STORAGE_PREFIX} from "../AuthTypes";
import {locationToURL} from "../../routing/routing.utils";

export type LoginRouteState = {
    state: 'LoginRouteState'
    loginIntention: {
        autoLogin: boolean
        onSuccessRedirectTo: string
        onSuccessRedirectOptions?: NavigateOptions
    }
}

export function isLoginRouteState(obj: any): obj is LoginRouteState {
    return obj?.state === 'LoginRouteState';
}

export const LOGIN_STATE_SESSION_KEY = SESSION_STORAGE_PREFIX + 'login_state';

export function buildLoginRouteState(location: Location, autoLogin: boolean): LoginRouteState {
    return {
        state: 'LoginRouteState',
        loginIntention: {
            autoLogin,
            onSuccessRedirectTo: locationToURL(location),
            onSuccessRedirectOptions: {
                state: location.state,
            },
        },
    };
}

export function getPersistedLoginRouteState(): LoginRouteState | null {
    const raw = sessionStorage.getItem(LOGIN_STATE_SESSION_KEY);
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        return isLoginRouteState(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

/** Keep the route the user was on when returning from Auth0 (callback may land on `/`). */
export function resolveLoginIntention(location: Location, autoLogin: boolean): LoginRouteState {
    if (hasAuthCallbackParams(location.search)) {
        return getPersistedLoginRouteState() ?? buildLoginRouteState(location, autoLogin);
    }
    return buildLoginRouteState(location, autoLogin);
}

/** Prefer router state; fall back to the login page without auto-login. */
export function resolveLoginRouteState(location: Location): LoginRouteState {
    if (isLoginRouteState(location.state)) {
        return location.state;
    }
    return buildLoginRouteState(location, false);
}

/** Auth0 callback URL for a given app path (requires `origin/*` in Allowed Callback URLs). */
export function auth0RedirectUriForPath(pathWithSearchHash: string): string {
    const path = pathWithSearchHash.split('?')[0].split('#')[0];
    if (!path.startsWith('/')) {
        return window.location.origin;
    }
    return `${window.location.origin}${path}`;
}

export function hasAuthCallbackParams(search: string): boolean {
    const params = new URLSearchParams(search);
    return (params.has('code') || params.has('error')) && params.has('state');
}
